/** Data Tier 2 — client cloud storage (Setup metadata + Google Drive live path). */

export const CLOUD_PROVIDERS = [
  { value: 'pending', label: 'Connect later' },
  { value: 'google_drive', label: 'Google Drive' },
  { value: 'google_workspace', label: 'Google Workspace' },
  { value: 'onedrive', label: 'OneDrive' },
  { value: 'icloud_drive', label: 'iCloud Drive' },
  { value: 'dropbox', label: 'Dropbox' },
] as const;

export type CloudProviderValue = (typeof CLOUD_PROVIDERS)[number]['value'];

export const CLOUD_STORAGE_FIELD_LABEL = 'Cloud storage: your own folder';

export const DEFAULT_MENTEE_FOLDER_LABEL = 'Mentor_Files';

export const CLOUD_FOLDER_PLACEHOLDER = 'e.g. Mentor_Files';

export const CLOUD_FOLDER_CREATE_HELPER =
  'Name the folder you want. When you connect Google Drive, Mentology creates it for you. Put your documents in that folder afterwards.';

/** Organisation-shared policies / learning pack (AMD-T2 C2). */
export const ORG_SHARED_FOLDER_PLACEHOLDER = 'e.g. Mentology Org Policies & Learning Pack';

export const CLOUD_STORAGE_HELPER_LINE =
  'Web search uses a server-side API key only — no client device permissions.';

/** Developer-only — never render on mentee Setup screens. */
export const CLOUD_OAUTH_DEV_NOTE =
  'Google Drive and Google Workspace support live OAuth. OneDrive, Dropbox, and iCloud remain intent-only stubs until those apps are registered. Secrets stay in server environment variables.';

/** Developer-only — never render on mentee Setup screens. */
export const CLOUD_STATUS_DEV_HELPER =
  'Status: Not connected · Google OAuth env not configured on server';

/** Developer-only — never render on mentee Setup screens. */
export const CLOUD_MAINTENANCE_PREVIEW_DEV_NOTE =
  'Google Drive cannot finish sign-in inside Maintenance Preview. Click Connect Google Drive to open the full app window, or log in as the mentee at /pro-setup.';

export const CLOUD_OAUTH_STUB_NOTE =
  'Connect your Google Drive so AI-Mentor can work from the documents you choose to share. Other cloud providers are coming soon.';

export const CLOUD_CONNECT_NEXT_STEP =
  'Click Connect Google Drive to get started, or set this up later from your Setup page.';

export const CLOUD_CONNECT_UNAVAILABLE =
  'Google Drive is not ready to connect right now. You can continue setup and connect later from this page.';

export const CLOUD_CONNECT_SIGNIN =
  'Sign in first, then click Connect Google Drive.';

export function menteeCloudStatusMessage(error: unknown, fallback = CLOUD_CONNECT_UNAVAILABLE): string {
  const raw = typeof error === 'string'
    ? error
    : error && typeof error === 'object' && 'error' in error
      ? String((error as { error?: string }).error || '')
      : String(error || '');
  if (/oauth|GOOGLE_CLIENT|not configured|environment variable|redirect_uri/i.test(raw)) {
    return CLOUD_CONNECT_UNAVAILABLE;
  }
  if (/authentication required|sign in|unauthorized/i.test(raw)) {
    return CLOUD_CONNECT_SIGNIN;
  }
  if (!raw || /unable to start google/i.test(raw)) return fallback;
  if (/tier\s*[12]|working-file|iframe/i.test(raw)) return fallback;
  return raw;
}

export const CLOUD_STORAGE_NOTE =
  "Business files remain in your own Drive/OneDrive; they are never duplicated into Mentology systems.";

export const BUSINESS_BRIEF_FILENAME = 'Mentology_Business_Brief';
export const BUSINESS_BRIEF_TEMPLATE_PATH = '/templates/Mentology_Business_Brief.txt';
export const BUSINESS_BRIEF_DOWNLOAD_NAME = 'Mentology_Business_Brief.txt';

export const BUSINESS_CONTEXT_HEADING = 'Ground your AI-Mentor — business context and files';

export const BUSINESS_CONTEXT_BANNER =
  'Download the 1-Page Business Brief template, fill in your business basics, and upload it here so your AI-Mentor understands your exact business model.';

export const BUSINESS_CONTEXT_PRIVACY =
  'Uploaded files remain 100% your private property inside your Google Drive. Mentology stores reference pointers only — never copies of the files themselves.';

export const FILE_ROLE_OPTIONS = [
  { value: 'business_brief', label: 'Business Context Brief (Recommended for Setup)' },
  { value: 'operational_manual', label: 'Operational Manual / SOPs' },
  { value: 'industry_reference', label: 'Industry Reference Files' },
  { value: 'document_templates', label: 'Document Templates / Policies' },
] as const;

export type FileRoleValue = (typeof FILE_ROLE_OPTIONS)[number]['value'];

export const FILE_ROLE_LABELS: Record<FileRoleValue, string> = {
  business_brief: 'Business Context Brief',
  operational_manual: 'Operational Manual / SOPs',
  industry_reference: 'Industry Reference Files',
  document_templates: 'Document Templates / Policies',
};

export function isFileRoleValue(value: unknown): value is FileRoleValue {
  return FILE_ROLE_OPTIONS.some((option) => option.value === value);
}

export function fileRoleLabel(value: unknown): string {
  if (isFileRoleValue(value)) return FILE_ROLE_LABELS[value];
  return 'General document';
}

export function connectedStorageStatusLine(cloud: CloudStorageMeta): string {
  if (cloud.connected && isGoogleCloudProvider(cloud.provider)) {
    return `Connected Storage: Google Drive | Target Folder: ${cloud.folder_label || DEFAULT_MENTEE_FOLDER_LABEL}`;
  }
  return 'Connected Storage: Not connected yet';
}

export const LIVE_CLOUD_PROVIDERS: CloudProviderValue[] = ['google_drive', 'google_workspace'];

export function isGoogleCloudProvider(provider: unknown): boolean {
  return provider === 'google_drive' || provider === 'google_workspace';
}

export function isLiveCloudProvider(provider: unknown): boolean {
  return isGoogleCloudProvider(provider);
}

export function isStubCloudProvider(provider: unknown): boolean {
  return provider === 'onedrive' || provider === 'dropbox' || provider === 'icloud_drive';
}

export interface CloudStorageMeta {
  provider: CloudProviderValue;
  folder_label: string;
  folder_id?: string | null;
  connected: boolean;
  note: string;
}

export interface Tier2SelectedFile {
  drive_file_id: string;
  filename: string;
  category?: string;
  role?: FileRoleValue | string;
  mime_type?: string | null;
  selected_at?: string;
}

export function isLocalIntentFileId(id: unknown): boolean {
  return String(id || '').startsWith('local-intent-');
}

export function googleDriveFolderUrl(folderId?: string | null): string {
  const id = String(folderId || '').trim();
  return id ? `https://drive.google.com/drive/folders/${id}` : 'https://drive.google.com';
}

export function normalizeCloudProvider(value: unknown): CloudProviderValue {
  const raw = typeof value === 'string' ? value.trim() : '';
  return CLOUD_PROVIDERS.some((p) => p.value === raw)
    ? (raw as CloudProviderValue)
    : 'pending';
}

export function buildCloudStorageMeta(input: {
  provider?: unknown;
  folder_label?: unknown;
  folder_id?: unknown;
  connected?: unknown;
}): CloudStorageMeta {
  const provider = normalizeCloudProvider(input.provider);
  const folder_label = String(input.folder_label ?? '').trim();
  const folder_id = String(input.folder_id ?? '').trim() || null;
  const requestedConnected = input.connected === true
    || input.connected === 1
    || input.connected === 'true';

  // Only Google Drive / Workspace can be connected=true, and only with a folder after OAuth.
  const connected = isGoogleCloudProvider(provider) && requestedConnected && Boolean(folder_id);

  return {
    provider,
    folder_label,
    folder_id: isGoogleCloudProvider(provider) ? folder_id : null,
    connected,
    note: CLOUD_STORAGE_NOTE,
  };
}
